import React from 'react';
import { Mic, MicOff, Type, Volume2, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { type Sentence } from '../../types';
import { useTypingMode } from '../../hooks/useTypingMode';
import { playSuccessSound, playWrongSound, playDoneSound } from '../../utils/audio';
import { useRef, useEffect } from 'react';
import { useTranslation } from '../../utils/translations';
import { normalizeForSpeechRecognition } from '../../utils/textProcessing';

interface AudioSegment {
  text: string;
  start: number;
  end: number;
  audio_url?: string;
}

interface SentencePracticeProps {
  sentence: Sentence;
  isInitializing: boolean;
  currentIndex: number;
  totalSentences: number;
  isAdmin: boolean;
  audioSegment: AudioSegment | null;
  onPlayAudio: (start: number, end: number) => void;
  onWordClick: (word: string) => void;
  onNext: () => void;
  isRecording: boolean;
  isAudioPlaying?: boolean;
  spokenText: string;
  matchPercentage: number;
  attempts: number;
  canSkip: boolean;
  sentenceCompleted: boolean;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onReleaseStream: () => void | Promise<void>;
  unknownWords: string[];
  setSentenceCompleted: (completed: boolean) => void;
  nativeLanguage?: string;
  audioReadyAt?: number;
}

export function SentencePractice({
  sentence,
  isInitializing,
  currentIndex,
  totalSentences,
  isAdmin,
  audioSegment,
  onPlayAudio,
  onWordClick,
  onNext,
  isRecording,
  isAudioPlaying = false,
  spokenText,
  matchPercentage,
  attempts,
  canSkip,
  sentenceCompleted,
  onStartRecording,
  onStopRecording,
  onReleaseStream,
  unknownWords,
  setSentenceCompleted,
  nativeLanguage,
  audioReadyAt
}: SentencePracticeProps) {
  const { t } = useTranslation(nativeLanguage);
  const {
    isTypingMode,
    slots,
    availableWords,
    typingCompleted,
    showCompletedSentence,
    isIncorrect,
    handleWordSelect,
    handleRemoveWord,
    switchToTypingMode,
    resetTypingMode
  } = useTypingMode(sentence.dutch, handleTypingComplete);

  const [showTranslation, setShowTranslation] = React.useState(true);
  const [hasPlayedAudio, setHasPlayedAudio] = React.useState(false);
  const [audioEnded, setAudioEnded] = React.useState(false);
  const [localInitializing] = React.useState(false);
  const recordingStoppedRef = useRef(false);
  const [recordingStopped, setRecordingStopped] = React.useState(false);
  const [manualPlayInitiated, setManualPlayInitiated] = React.useState(false);
  const [micInitialized, setMicInitialized] = React.useState(false);
  const micAttempts = React.useRef(0);
  const successSoundPlayedRef = React.useRef(false);
  const mountedRef = React.useRef(true);
  const cleanupTimeoutRef = React.useRef<number | null>(null);
  const successTimeoutRef = React.useRef<number | null>(null);
  const navigationTimeoutRef = React.useRef<number | null>(null);
  const lastPlayedSegmentRef = React.useRef<string | null>(null);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const autoplayTimeoutRef = React.useRef<number | null>(null);
  const [autoplayFailed, setAutoplayFailed] = React.useState(false);
  // Stable ref for onPlayAudio to avoid effect thrashing when parent re-renders
  const onPlayAudioRef = React.useRef(onPlayAudio);
  React.useEffect(() => { onPlayAudioRef.current = onPlayAudio; });

  const isSupported = 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
  const donePlayedRef = React.useRef(false);

  // Create an Audio element that plays through speaker on iOS even during mic recording.
  // Using a DOM-attached element with playsinline is required — new Audio() is not enough.
  const createSpeakerAudio = (): HTMLAudioElement => {
    const audio = document.createElement('audio');
    audio.setAttribute('playsinline', '');
    audio.setAttribute('webkit-playsinline', '');
    return audio;
  };
  
  function handleTypingComplete() {
    playDoneSound();
    setSentenceCompleted(true);
  }
  
  React.useEffect(() => {
    mountedRef.current = true;

    // Reset success state when component mounts or currentIndex changes
    successSoundPlayedRef.current = false;

    // Make sure we clean up any recording when component unmounts
    return () => {
      mountedRef.current = false;
      recordingStoppedRef.current = true;
      // Intentionally NOT calling onStopRecording here.
      // The parent (proceedToNext) already stops recording before unmounting this component.
      // Double-calling stopRecording increments sessionIdRef twice, which causes all
      // subsequent recognition events for the new sentence to be silently dropped.

      // Clear any pending success timeouts
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = null;
      }

      // Clear navigation timeout
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
        navigationTimeoutRef.current = null;
      }

      // Clear any pending timeouts
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
        cleanupTimeoutRef.current = null;
      }

      // Clear autoplay timeout
      if (autoplayTimeoutRef.current) {
        clearTimeout(autoplayTimeoutRef.current);
        autoplayTimeoutRef.current = null;
      }

      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  // Force stop recording when sentence is completed
  React.useEffect(() => {
    if (sentenceCompleted && isRecording && !recordingStopped) {
      console.log('Sentence completed - stopping recording and preventing further recordings');
      setRecordingStopped(true);
      recordingStoppedRef.current = true;
      // Add a slight delay to ensure any processing is complete
      const stopTimeout = setTimeout(() => {
        if (mountedRef.current && isRecording) {
          onStopRecording();
        }
        // Release the stream immediately so iOS exits PlayAndRecord mode
        // as early as possible — giving maximum time before the next audio plays
        onReleaseStream();
      }, 500);

      return () => {
        clearTimeout(stopTimeout);
      };
    }
  }, [sentenceCompleted, isRecording, onStopRecording, onReleaseStream, recordingStopped]);

  // Reset recordingStopped when currentIndex changes
  React.useEffect(() => {
    setRecordingStopped(false);
    successSoundPlayedRef.current = false;
    recordingStoppedRef.current = false;
    donePlayedRef.current = false;
    setMicInitialized(false);
    setAutoplayFailed(false);

    // Reset sentence completion state when moving to new sentence
    setSentenceCompleted(false);

    // Clear any pending success timeouts
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }

    // Clear navigation timeout
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
      navigationTimeoutRef.current = null;
    }
  }, [currentIndex]);
  
  // Play done sound when 75% threshold is reached (but continue recording)
  React.useEffect(() => {
    if (
      matchPercentage >= 75 &&
      matchPercentage < 100 &&
      isRecording &&
      !donePlayedRef.current &&
      !successSoundPlayedRef.current &&
      spokenText.trim().length > 0
    ) {
      donePlayedRef.current = true;
      setSentenceCompleted(true); // Activate the Next button
      // Continue recording - don't stop here
    }
  }, [matchPercentage, isRecording, setSentenceCompleted, spokenText]);

  // Play done sound when sentence is completed (button becomes activated)
  
  // Handle auto-play of audio for current sentence
  React.useEffect(() => {
    if (!audioSegment) return;

    const segmentKey = audioSegment.audio_url || `${audioSegment.start}-${audioSegment.end}`;

    if (lastPlayedSegmentRef.current === segmentKey) return;

    if (autoplayTimeoutRef.current) {
      clearTimeout(autoplayTimeoutRef.current);
      autoplayTimeoutRef.current = null;
    }

    const capturedSegment = audioSegment;
    const capturedKey = segmentKey;

    const doPlay = () => {
      if (!mountedRef.current || !capturedSegment) return;
      if (lastPlayedSegmentRef.current === capturedKey) return;

      lastPlayedSegmentRef.current = capturedKey;
      setHasPlayedAudio(true);
      setAutoplayFailed(false);

      const playReal = () => {
        if (!mountedRef.current) return;
        if (capturedSegment.audio_url) {
          if (!audioRef.current) audioRef.current = createSpeakerAudio();
          const audio = audioRef.current;
          audio.pause();
          audio.src = capturedSegment.audio_url;

          audio.onended = () => {
            if (mountedRef.current) setAudioEnded(true);
          };

          audio.onerror = () => {
            if (mountedRef.current) {
              setAutoplayFailed(true);
              setAudioEnded(false);
            }
          };

          const tryPlay = () => {
            audio.play().catch(() => {
              if (mountedRef.current) setAutoplayFailed(true);
            });
          };

          if (audio.readyState >= 3) {
            tryPlay();
          } else {
            audio.addEventListener('canplay', tryPlay, { once: true });
            audio.load();
          }
        } else {
          onPlayAudioRef.current(capturedSegment.start, capturedSegment.end);
        }
      };

      // iOS audio session trick: briefly play a silent audio clip first to
      // force the audio session to switch from PlayAndRecord to Playback route
      // (speaker). Without this, audio can route to the earpiece if the mic
      // session hasn't fully torn down yet.
      const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      if (isMobile) {
        const silentDataUrl =
          'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';
        const primer = createSpeakerAudio();
        primer.src = silentDataUrl;
        primer.volume = 0;
        primer.play().catch(() => {});
        setTimeout(() => {
          primer.pause();
          playReal();
        }, 50);
      } else {
        playReal();
      }
    };

    // On mobile, ensure at least 700ms has elapsed since the mic stream was
    // released so iOS fully exits PlayAndRecord mode before audio plays.
    // On desktop, a minimal delay is fine.
    const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const MIN_DELAY_AFTER_RELEASE = 700;
    const elapsed = audioReadyAt != null ? Date.now() - audioReadyAt : MIN_DELAY_AFTER_RELEASE;
    const delay = isMobile ? Math.max(150, MIN_DELAY_AFTER_RELEASE - elapsed) : 100;
    autoplayTimeoutRef.current = window.setTimeout(doPlay, delay);

    return () => {
      if (autoplayTimeoutRef.current) {
        clearTimeout(autoplayTimeoutRef.current);
        autoplayTimeoutRef.current = null;
      }
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
    // onPlayAudio intentionally excluded — we use onPlayAudioRef.current for stability
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, audioSegment, audioReadyAt]);
  
  // Detect when shared audio player finishes playing (for timestamp-based lessons)
  const prevIsAudioPlayingRef = useRef(false);
  React.useEffect(() => {
    if (prevIsAudioPlayingRef.current && !isAudioPlaying && hasPlayedAudio) {
      setAudioEnded(true);
    }
    prevIsAudioPlayingRef.current = isAudioPlaying;
  }, [isAudioPlaying, hasPlayedAudio]);

  // Reset hasPlayedAudio when currentIndex changes
  React.useEffect(() => {
    if (!mountedRef.current) return;

    console.log('Resetting audio flags for new sentence:', currentIndex);
    setHasPlayedAudio(false);
    setAudioEnded(false);
    setManualPlayInitiated(false);
    successSoundPlayedRef.current = false;
    lastPlayedSegmentRef.current = null;
  }, [currentIndex]);
  
  // Handle manual audio play
  const handlePlayAudio = () => {
    if (audioSegment) {
      setManualPlayInitiated(true);
      setAutoplayFailed(false);

      const segmentKey = audioSegment.audio_url || `${audioSegment.start}-${audioSegment.end}`;

      if (audioSegment.audio_url) {
        if (!audioRef.current) {
          audioRef.current = createSpeakerAudio();
        }

        const audio = audioRef.current;
        audio.pause();
        audio.src = audioSegment.audio_url;

        audio.onended = () => {
          console.log('Manual audio playback ended');
          if (mountedRef.current) setAudioEnded(true);
        };

        audio.onerror = (error) => {
          console.error('Error playing manual audio:', error);
        };

        const tryPlay = () => {
          audio.play().catch((error) => {
            console.error('Failed to play manual audio:', error);
          });
        };

        if (audio.readyState >= 3) {
          tryPlay();
        } else {
          audio.addEventListener('canplay', tryPlay, { once: true });
          audio.load();
        }

        lastPlayedSegmentRef.current = segmentKey;
        setHasPlayedAudio(true);
      } else {
        onPlayAudio(0, 0);
        setTimeout(() => {
          if (audioSegment) {
            onPlayAudio(audioSegment.start, audioSegment.end);
            lastPlayedSegmentRef.current = segmentKey;
            setHasPlayedAudio(true);
          }
        }, 50);
      }
    }
  };

// Handle 100% match - stop recording and release stream FIRST, then play sound
React.useEffect(() => {
  if (matchPercentage >= 100 && !successSoundPlayedRef.current) {
    successSoundPlayedRef.current = true;
    donePlayedRef.current = true;
    setSentenceCompleted(true);
    console.log('Auto-stopping recording at 100% match');
    onStopRecording();
    setRecordingStopped(true);
    recordingStoppedRef.current = true;
    onReleaseStream();
    // Delay the success sound so iOS has time to switch the audio session
    // from PlayAndRecord back to Playback before any audio plays.
    // Playing audio while the mic session is still tearing down routes sound
    // to the earpiece and breaks subsequent recording.
    setTimeout(() => {
      if (mountedRef.current) playSuccessSound();
    }, 300);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [matchPercentage]);

  
// Switches to speaking mode — does NOT start recording
const handleSpeakingModeClick = () => {
  if (isTypingMode) {
    resetTypingMode();
    setSentenceCompleted(false);
    setRecordingStopped(false);
    recordingStoppedRef.current = false;
    successSoundPlayedRef.current = false;
  }
};

// Starts or stops the actual voice recording
const handleRecordClick = () => {
  if (isInitializing || localInitializing) {
    return;
  }

  if (isRecording) {
    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
    }

    setRecordingStopped(false);
    recordingStoppedRef.current = false;
    successSoundPlayedRef.current = false;

    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }

    onStopRecording();
    onReleaseStream();
    return;
  }

  if (!isSupported) {
    if (mountedRef.current) {
      alert('Speech recognition is not supported on this device or browser. Please use typing mode instead.');
    }
    return;
  }

  // Stop any playing audio before starting the mic — on iOS, SpeechRecognition
  // silently fails if called while audio is playing in the same audio session.
  if (audioRef.current) {
    audioRef.current.pause();
  }
  onPlayAudioRef.current(0, 0);

  setSentenceCompleted(false);
  setRecordingStopped(false);
  recordingStoppedRef.current = false;
  successSoundPlayedRef.current = false;
  setMicInitialized(true);
  micAttempts.current = 0;

  onStartRecording();
};
  
  const handleWordClick = (word: string) => {
    // Allow word clicks in speaking mode and when not recording
    if (!isTypingMode && !isRecording) {
      console.log('Word clicked:', word);
      // Clean the word from ALL punctuation and special characters before marking as unknown
      const cleanWord = normalizeForSpeechRecognition(word);
      onWordClick(cleanWord);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
        {!isRecording && !isTypingMode && (
          <div className="text-sm text-gray-500 mb-4">
            {t('Tip: Tap on words to mark them as unknown')}
          </div>
        )}

        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {t('Sentence')} {currentIndex + 1} {t('of')} {totalSentences}
          </div>
          <div className="flex gap-2">
            <button
              onClick={switchToTypingMode}
              className={`flex items-center px-3 py-1 rounded-full transition-colors
                ${isTypingMode ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
              `}
            >
              <Type className="h-4 w-4 mr-2" />
              {t('Typing')}
            </button>
            <button
              onClick={handleSpeakingModeClick}
              className={`flex items-center px-3 py-1 rounded-full transition-colors
                ${!isTypingMode ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
              `}
            >
              <Mic className="h-4 w-4 mr-2" />
              {t('Speaking')}
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {autoplayFailed && (
            <div className="text-xs text-amber-600 font-medium">Tap the speaker icon to play audio</div>
          )}
          <div className="flex items-center gap-2">
            <button
              onClick={handlePlayAudio}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <Volume2 className={`h-5 w-5 ${!hasPlayedAudio ? 'animate-audio-attention' : ''}`} />
            </button>
            {!isTypingMode ? (
              <p className="text-xl">
                {sentence.dutch.split(/\s+/).map((word, index) => {
                  // Normalize both the word and spoken text for comparison
                  const normalizedWord = normalizeForSpeechRecognition(word);
                  const normalizedSpokenText = normalizeForSpeechRecognition(spokenText);
                  const isRecognized = normalizedSpokenText.includes(normalizedWord);
                  return (
                    <span
                      key={index}
                      onClick={() => handleWordClick(word)}
                      className={`cursor-pointer transition-colors ${
                        unknownWords.includes(normalizeForSpeechRecognition(word))
                          ? 'text-red-500'
                          : isRecording && isRecognized
                          ? 'text-green-600 font-medium'
                          : 'hover:text-indigo-600'
                      }`}
                    >
                      {word}{' '}
                    </span>
                  );
                })}
              </p>
            ) : (
              <p className="text-xl font-semibold text-gray-700">{t('Build the sentence below')}</p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowTranslation(!showTranslation)}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              {showTranslation ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
            {showTranslation && <p className="text-gray-600 italic">{sentence.english}</p>}
          </div>

          {isTypingMode ? (
            <div className="space-y-4">
              <div className={`flex flex-wrap gap-2 p-4 border-2 rounded-lg transition-colors ${
                isIncorrect ? 'border-red-500 animate-shake' : showCompletedSentence ? 'border-green-500' : 'border-gray-300'
                }`}
              >
                {!showCompletedSentence && !isIncorrect ? (
                  slots.map((slot, index) => {
                    if (slot.type === 'pre-filled') {
                      return (
                        <span key={index} className="px-2 py-1 bg-gray-200 rounded">
                          {slot.word}
                        </span>
                      );
                    } else if (slot.type === 'user-filled') {
                      return (
                        <span
                          key={index}
                          onClick={() => handleRemoveWord(index)}
                          className="px-2 py-1 bg-indigo-100 rounded cursor-pointer hover:bg-indigo-200"
                        >
                          {slot.word}
                        </span>
                      );
                    } else {
                      return (
                        <span key={index} className="px-2 py-1 bg-white border-2 border-dashed border-gray-300 rounded">
                          _____
                        </span>
                      );
                    }
                  })
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {slots.map((slot, index) => (
                      <span key={index} className="px-2 py-1 text-xl font-semibold text-gray-700">
                        {index === 0 ? slot.word.charAt(0).toUpperCase() + slot.word.slice(1) : slot.word}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {!showCompletedSentence && !isIncorrect && (
                <div className="flex flex-wrap gap-2">
                  {availableWords.map(word => (
                    <button
                      key={word.id}
                      onClick={() => handleWordSelect(word)}
                      className="px-3 py-1 text-base font-medium rounded-full bg-white hover:bg-indigo-50 border-2 border-indigo-300 hover:border-indigo-400 transition-all"
                    >
                      {word.word}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={handleRecordClick}
                  className={`flex items-center px-4 py-2 rounded-full transition-colors ${
                    isRecording || isInitializing ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-600 hover:bg-blue-700'
                  } text-white ${
                    (sentenceCompleted && matchPercentage >= 100) ? 'opacity-50 cursor-not-allowed' : ''
                  } ${audioEnded && !isRecording && !sentenceCompleted && !isTypingMode ? 'animate-mic-attention' : ''}`}
                  disabled={isInitializing || localInitializing || (sentenceCompleted && matchPercentage >= 100)}
                >
                  {isInitializing || localInitializing ? (
                    <>
                      <div className="h-5 w-5 mr-2 animate-pulse rounded-full bg-white"></div>
                      {t('Initializing...')}
                    </>
                  ) : isRecording ? (
                    <>
                      <MicOff className="h-5 w-5 mr-2" />
                      {t('Stop Recording')}
                    </>
                  ) : (
                    <>
                      <Mic className="h-5 w-5 mr-2" />
                      {sentenceCompleted && matchPercentage >= 100 ? t('Completed ✓') : sentenceCompleted ? t('Record Again') : t('Start Recording')}
                    </>
                  )}
                </button>
                {isRecording && (
                  <p className="text-indigo-600">
                    {t('Listening... Say the sentence in Dutch')}
                  </p>
                )}
                {(isInitializing || localInitializing) && (
                  <p className="text-indigo-600">
                    <span className="animate-pulse">{t('Initializing microphone...')}</span>
                  </p>
                )}
                {!isSupported && (
                  <p className="text-yellow-600">{t('Speech recognition not available. Please use typing mode.')}</p>
                )}
              </div>
              {spokenText && (
                <div className="text-gray-700">
                  <p>{t('You said:')} {spokenText}</p>
                  <div className="mt-2">
                    <p>{t('Match:')} {Math.round(matchPercentage)}%</p>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mt-1">
                      <div
                        className="bg-indigo-600 h-2.5 rounded-full"
                        style={{ width: `${matchPercentage}%` }}
                      ></div>
                    </div>
                    {matchPercentage >= 75 && <p className="text-green-600 mt-1">{t('Great job!')}</p>}
                    {matchPercentage < 75 && attempts >= 3 && (
                      <p className="text-yellow-600 mt-1">{t('You can skip if needed')}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-between items-center">
          <button 
            onClick={async () => {
              // Immediately disable the button to prevent multiple clicks
              setSentenceCompleted(false);

              // Always stop recording before moving to next sentence
              if (isRecording && !recordingStopped) {
                console.log('Stopping recording before moving to next');
                onStopRecording();
                setRecordingStopped(true);
                // Set flag to prevent multiple clicks
                recordingStoppedRef.current = true;
              }

              // Clear any existing timeout
              if (cleanupTimeoutRef.current) {
                clearTimeout(cleanupTimeoutRef.current);
                cleanupTimeoutRef.current = null;
              }

              // Clear navigation timeout
              if (navigationTimeoutRef.current) {
                clearTimeout(navigationTimeoutRef.current);
                navigationTimeoutRef.current = null;
              }

              // Use navigation timeout to ensure clean state transition
              navigationTimeoutRef.current = window.setTimeout(() => {
                navigationTimeoutRef.current = null;
                cleanupTimeoutRef.current = null;
                console.log('Timeout finished, proceeding to next sentence');

                // Clear any pending success timeouts
                if (successTimeoutRef.current) {
                  clearTimeout(successTimeoutRef.current);
                  successTimeoutRef.current = null;
                }

                // Ensure we start fresh on the next page
                recordingStoppedRef.current = false;
                successSoundPlayedRef.current = false;
                donePlayedRef.current = false;

                onNext();
              }, 200);
            }}
            disabled={
              !isAdmin &&
              ((isTypingMode && !typingCompleted) ||
               (!isTypingMode && !sentenceCompleted && !canSkip) ||
               (!isTypingMode && (isInitializing || localInitializing)))
            }
            className={`flex items-center px-4 py-2 rounded-full transition-colors ${
              !isAdmin &&
              ((isTypingMode && !typingCompleted) ||
               (!isTypingMode && !sentenceCompleted && !canSkip) ||
               (!isTypingMode && (isInitializing || localInitializing)))
                ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                : 'bg-green-500 hover:bg-green-600 text-white'
            }`}
          >
            {t('Next Sentence')}
            <ArrowRight className="h-4 w-4 ml-2" />
          </button>
        </div>

        {unknownWords.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-gray-700">{t('Unknown words:')}</span>
              {unknownWords.map((word, index) => (
                <span key={index} className="px-2 py-1 text-sm bg-gray-100 text-gray-600 rounded-full">
                  {word}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="text-xs text-gray-500 space-y-1">
            <p className="font-medium">{t('Tips:')}</p>
            <p>1. {t('Switch to type mode if there are issues with recording')}</p>
            <p>2. {t('You can repeat the same word multiple times to increase the match percentage')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}